import { Link } from 'react-router-dom';
import { Play, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CraftPage = () => {
  return (
    <div className="pt-32 pb-24" data-testid="craft-page">
      {/* Hero with Video */}
      <section className="chaleur-container mb-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-24 items-center">
          <div>
            <p className="caption text-primary mb-4">The Craft</p>
            <h1 className="text-4xl md:text-5xl font-light mb-6">
              Where Tradition Meets <span className="editorial-italic">Artistry</span>
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              Every Chaleur piece is born from centuries-old techniques, passed down through 
              generations of master artisans. Discover the heritage, skill, and passion 
              woven into each thread.
            </p>
          </div>
          <div className="aspect-video overflow-hidden relative rounded-lg">
            <video 
              className="w-full h-full object-cover"
              autoPlay 
              muted 
              loop 
              playsInline
              poster="/images/products/artisan_weaver_man.jpg"
            >
              <source src="/videos/artisan_craft_4.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Artisan Portrait Section */}
      <section className="relative h-[50vh] mb-24 overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="overflow-hidden">
            <img 
              src="/images/products/artisan_embroidery_woman.jpg" 
              alt="Artisan doing Sozni embroidery"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="overflow-hidden">
            <img 
              src="/images/products/artisan_weaver_man.jpg" 
              alt="Master weaver at traditional loom"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative h-full flex items-center justify-center text-center text-white">
          <div>
            <p className="caption text-white/80 mb-4">200+ Artisans</p>
            <h2 className="text-4xl md:text-5xl font-light">Master Hands, Timeless Craft</h2>
          </div>
        </div>
      </section>

      {/* Craft Techniques */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">Our Craft Techniques</p>
            <h2 className="text-3xl md:text-4xl font-light">The Art Behind Each Piece</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Handweaving',
                description: 'Traditional pit looms create intricate patterns impossible to replicate by machine.',
                image: '/images/products/artisan_loom_cream.jpg'
              },
              {
                name: 'Block Printing',
                description: 'Hand-carved wooden blocks create unique, organic patterns on fine fabrics.',
                image: '/images/products/IMG_6246.jpg'
              },
              {
                name: 'Sozni Embroidery',
                description: 'Delicate needlework adds dimension and storytelling to each piece.',
                image: '/images/products/artisan_embroidery_woman.jpg'
              },
              {
                name: 'Natural Dyeing',
                description: 'Plant-based dyes create rich, sustainable colors that age beautifully.',
                image: '/images/products/IMG_6249.jpg'
              },
              {
                name: 'Pashmina Weaving',
                description: 'Ultra-fine Pashmina wool from Ladakh, hand-spun and woven.',
                image: '/images/products/artisan_yarn_hands.jpg'
              },
              {
                name: 'Kantha',
                description: 'Running stitch embroidery transforms simple fabrics into works of art.',
                image: '/images/products/IMG_6252.jpg'
              }
            ].map((craft, idx) => (
              <div key={idx} className="group">
                <div className="aspect-[4/3] overflow-hidden mb-4">
                  <img 
                    src={craft.image}
                    alt={craft.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl mb-2">{craft.name}</h3>
                <p className="text-sm text-muted-foreground">{craft.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/products/IMG_6246.jpg"
                alt="Premium materials"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="caption text-primary mb-4">Our Materials</p>
              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Only the <span className="editorial-italic">Finest</span> Materials
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We source the world's most luxurious natural fibers—from the silkworms of 
                Karnataka to the Pashmina goats of Kashmir. Each material is selected for 
                its quality, sustainability, and connection to its land of origin.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary flex items-center justify-center text-primary font-medium">01</div>
                  <div>
                    <h4 className="font-medium">Pure Silk</h4>
                    <p className="text-sm text-muted-foreground">Sourced from South India's finest producers</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary flex items-center justify-center text-primary font-medium">02</div>
                  <div>
                    <h4 className="font-medium">Pashmina</h4>
                    <p className="text-sm text-muted-foreground">Hand-spun from Himalayan Changthangi goats</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary flex items-center justify-center text-primary font-medium">03</div>
                  <div>
                    <h4 className="font-medium">Merino Wool</h4>
                    <p className="text-sm text-muted-foreground">Ethically sourced, ultra-soft comfort</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-white/60 mb-4">The Journey</p>
            <h2 className="text-3xl md:text-4xl font-light">From Loom to You</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Design', desc: 'Our designers blend tradition with contemporary aesthetics' },
              { step: '02', title: 'Source', desc: 'Finest natural materials, ethically sourced' },
              { step: '03', title: 'Craft', desc: 'Master artisans bring each design to life' },
              { step: '04', title: 'Deliver', desc: 'Carefully packaged and shipped to your door' }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="text-4xl font-light text-primary mb-4">{item.step}</div>
                <h3 className="text-xl mb-2">{item.title}</h3>
                <p className="text-sm text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing">
        <div className="chaleur-container text-center">
          <p className="caption text-primary mb-4">Experience the Craft</p>
          <h2 className="text-3xl md:text-4xl font-light mb-6">
            Discover Our Collection
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
            Each Chaleur piece carries the story of its maker. Explore our collection 
            and find the piece that speaks to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="btn-chaleur-primary">
              <Link to="/shop">Shop Collection</Link>
            </Button>
            <Button asChild variant="outline" className="btn-chaleur-secondary">
              <Link to="/artisans">Meet the Artisans</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CraftPage;
